import '@/styles/auction-layout.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IngameFlash, useIngameFlash } from '@/components/IngameFlash';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { useAuction, useAuctionMutations } from '@/hooks/useAuction';
import { assetUrl, itemIconFallbackUrl, resolveMediaUrl } from '@/lib/assetUrl';

function formatTimeLeftFromEnd(endIso: string | null): string {
  if (!endIso) return '';
  const diff = new Date(endIso).getTime() - Date.now();
  if (diff <= 0) return '0m';

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);

  let result = '';
  if (days > 0) result += `${days}d `;
  result += `${hours}h ${minutes}m`;
  return result.trim();
}

export function AuctionPage() {
  const { t } = useLocale();
  const { data, isLoading, refetch } = useAuction();
  const { placeBid } = useAuctionMutations();

  const [bidInput, setBidInput] = useState('');
  const { flash, showFlash } = useIngameFlash(3000);
  const [timerLabel, setTimerLabel] = useState('');

  const auction = data?.auction;
  const auctionItems = data?.auction_items ?? [];
  const auctionBids = data?.auction_bids ?? [];

  const minBid = useMemo(() => {
    if (!auction || auction.status !== 'active') return 1;
    return auction.current_bid + auction.min_increment;
  }, [auction]);

  useEffect(() => {
    if (!auction?.auction_end || auction.status !== 'active') {
      setTimerLabel(auction?.time_left ?? '');
      return;
    }

    const tick = () => {
      const label = formatTimeLeftFromEnd(auction.auction_end);
      setTimerLabel(label);
      if (label === '0m') {
        void refetch();
      }
    };

    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, [auction?.auction_end, auction?.status, auction?.time_left, refetch]);

  const handleBid = useCallback(async () => {
    if (!auction || auction.status !== 'active') return;

    const bidAmount = parseInt(bidInput, 10);
    if (!bidAmount || Number.isNaN(bidAmount)) {
      showFlash(t('auc_invalid_bid'), 'danger');
      return;
    }

    try {
      const result = await placeBid.mutateAsync({
        auction_id: auction.id,
        bid_amount: bidAmount,
      });

      if (result.success) {
        showFlash(result.message || t('auc_bid_success'), 'success');
        setBidInput('');
      } else {
        showFlash(result.message || t('auc_bid_error'), 'danger');
      }
    } catch {
      showFlash(t('auc_bid_error'), 'danger');
    }
  }, [auction, bidInput, placeBid, showFlash, t]);

  const isActive = auction?.status === 'active';
  const isConcluded = auction?.status === 'concluded';
  const isUpcoming = !auction || auction.status === 'upcoming';

  return (
    <div className="container-ingame fade-in">
      <IngameFlash flash={flash} />

      <div className="auction-container">
        <SimpleMenu title={t('auction_system')} />

        <div className="auction-content">
          <div className="auction-left">
            {isLoading && !data ? (
              <div className="auction-loading-panel auction-status">
                <span className="auction-skeleton auction-skeleton--title" aria-hidden />
                <span className="auction-skeleton auction-skeleton--timer" aria-hidden />
              </div>
            ) : (
              <>
                <div className={`auction-status auction-${auction?.status ?? 'upcoming'}`}>
                  {isActive ? (
                    <>
                      <span className="auction-status-active">{auction.auction_name}</span>
                      <div className="auction-timer">
                        {t('auc_time_left')}: {timerLabel || auction.time_left}
                      </div>
                    </>
                  ) : isConcluded ? (
                    <>
                      <span className="auction-status-concluded">{t('auc_bid_concluded')}</span>
                      <div className="auction-winner">
                        {t('auc_winner')}: <b>{auction?.winner ?? ''}</b>
                      </div>
                    </>
                  ) : (
                    <span className="auction-status-upcoming">{t('auc_auction_starting_soon')}</span>
                  )}
                </div>

                {isActive ? (
                  <>
                    <div className="auction-bid-box">
                      <div className="auction-bid-row">
                        <input
                          type="number"
                          min={minBid}
                          step={auction.min_increment}
                          className="auction-bid-input"
                          placeholder={t('auc_enter_bid')}
                          value={bidInput}
                          onChange={(e) => setBidInput(e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="auction-bid-btn"
                        data-auction-id={auction.id}
                        disabled={placeBid.isPending}
                        onClick={() => void handleBid()}
                      >
                        {placeBid.isPending ? '...' : t('auc_bid_now')}
                      </button>
                    </div>

                    {auction.no_bids ? (
                      <div className="auction-current-bid text-info">{t('auc_be_first_to_bid')}</div>
                    ) : (
                      <div className="auction-current-bid auction-current-bid--centered">
                        {t('auc_current_bid')}:{' '}
                        <span className="auction-bid-amount">
                          {auction.current_bid.toLocaleString()}
                        </span>{' '}
                        {t('auc_by')}{' '}
                        <span className="auction-bid-user">{auction.current_user}</span>
                      </div>
                    )}

                    {auctionBids.length > 0 ? (
                      <div className="auction-bids-center">
                        <div className="auction-bids-box">
                          <div className="auction-bids-title">{t('auc_latest_bids')}</div>
                          <table className="auction-bids-table">
                            <tbody>
                              {auctionBids.slice(0, 5).map((bid, idx) => (
                                <tr key={`${bid.CharName16}-${bid.bid_amount}-${idx}`}>
                                  <td>
                                    <img
                                      className="auction-bids-avatar-img"
                                      src={assetUrl(
                                        `/assets/images/character/icon/${bid.RefObjID}.webp`,
                                      )}
                                      alt=""
                                      onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.style.visibility = 'hidden';
                                      }}
                                    />
                                    <span
                                      className="auction-bids-name"
                                      title={bid.CharName16}
                                    >
                                      {bid.CharName16}
                                    </span>
                                  </td>
                                  <td className="auction-bids-amount">
                                    {bid.bid_amount.toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : isConcluded ? (
                  <div className="auction-bid-concluded-msg">{t('auc_auction_ended')}</div>
                ) : isUpcoming ? (
                  <div className="auction-upcoming-msg">{t('auc_wait_next_auction')}</div>
                ) : null}
              </>
            )}
          </div>

          <div className="auction-right">
            {auction?.auction_image ? (
              <div className="auction-promo-img">
                <img src={resolveMediaUrl(auction.auction_image)} alt="Promo" />
              </div>
            ) : null}

            <div className="auction-item-name">{t('auc_item_rewards')}</div>

            <div className="auction-inline-items">
              {auctionItems.length > 0 ? (
                auctionItems.map((item) => (
                  <div className="auction-reward-wrap" key={`${item.codename128}-${item.sn_name}`}>
                    <img
                      className="auction-reward-img"
                      src={resolveMediaUrl(item.url_image)}
                      alt={item.sn_name}
                      title={item.sn_name}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = itemIconFallbackUrl();
                      }}
                    />
                  </div>
                ))
              ) : (
                <span className="auction-no-rewards-label">{t('auc_no_rewards')}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
